import { useDispatch, useSelector } from 'react-redux';
import {
  fetchClientByClientIdRequest,
  fetchTypeformPayloadByClientIdRequest,
  selectClientDataExists,
  selectClientIsLoading,
  selectMaybeClient,
  selectTypeformPayload,
  selectTypeformPayloadIsLoading,
  SheetModal,
} from '..';
import { useEffect, useMemo, useState } from 'react';
import {
  possessive,
  fullName,
  ClientStatus,
  QUICK_EDIT_ALL_FIELDS,
  usesEdjQuickPlanModelInput,
} from '@shared';
import { useNavigateToIntakeForm } from '@client/hooks/useNavigateToIntakeForm';
import { ALGORITHMS_ERROR } from '@client/util/constants';
import { useRecalculateQuickEstimate } from '@/features/client-quick-plan/hooks/use-recalculate-quick-estimate';
import { findClientIntakeSurvey } from '@/shared/lib/utils';
import { useQuickEditIntake } from '@client/hooks/useQuickEditIntake';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { Button } from './ui/button';
import { IntakeChangeRequestChanges } from './intake-change-request-changes';
import { Clock, Loader2, Pencil, Send, X } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertTitle,
  Button as DesignButton,
} from '@design-system';
import { QuickEditForm, QUICK_EDIT_FORM_ID, type QuickEditChange } from './QuickEditForm';
import {
  useIsPilotCapabilityReopenedForSelfPlan,
  useIsPilotRestricted,
} from '@/shared/hooks/use-active-pilot-program';

import clientIntakeFormSchema from '@shared/utils/clientIntakeFormSchema.json';
import { isArray } from 'lodash';

const PREFERENCE_DISPLAY_LABELS: Record<string, Record<string, string>> = {
  clientIndependencePreference: {
    home: 'Home-focused',
    balances: 'Balanced',
    comprehensive: 'Comprehensive care',
  },
  clientFinancialSecurityPreference: {
    affordable: 'Protect assets',
    balance: 'Balanced',
    prioritize: 'Best care',
  },
  clientFamilyInvolvementPreference: {
    'no direct care': 'None',
    'minimal': 'Minimal',
    'balance': 'Balanced',
    'heavily': 'Heavily involved',
  },
};

export function ClientDataModal({
  partialClient,
  open,
  initialMode = 'view',
  quickEstimateOnly = false,
  onClose,
}: {
  partialClient: Client;
  open: boolean;
  initialMode?: 'view' | 'edit';
  /**
   * Opened from "Edit Quick Estimate" (EDJ Quick Plan clients): always the EDJ edit form, no link
   * to the full questionnaire (EDJ restricts it), and Cancel or Save closes the modal instead of
   * falling back to the intake-data view. When the estimate failed ("Algorithms error"), saving
   * with no changes retries it from the saved answers.
   */
  quickEstimateOnly?: boolean;
  onClose: () => void;
}) {
  const dispatch = useDispatch();
  const clientIsLoading = useSelector(selectClientIsLoading);
  const clientDataExists = useSelector(selectClientDataExists);
  const typeformPayload = useSelector(selectTypeformPayload);
  const typeformPayloadIsLoading = useSelector(selectTypeformPayloadIsLoading);
  const { navigateToIntakeForm, isLoading: isLoadingForm } = useNavigateToIntakeForm({
    clientId: partialClient.clientId,
  });
  const { flags } = useFeatureFlags();
  const useFormProvider = flags.useFormProvider ?? false;
  const isIntakeFormReopenedForSelfPlan = useIsPilotCapabilityReopenedForSelfPlan(
    'intake-form',
    partialClient,
  );
  const isIntakeFormRestricted =
    useIsPilotRestricted('intake-form') && !isIntakeFormReopenedForSelfPlan;
  const isQuickEditRestricted = useIsPilotRestricted('quick-edit-intake');

  const isClientActive = partialClient.clientStatus === ClientStatus.active;

  const hasIntakeSurvey =
    (!!partialClient.intakeSurvey || isClientActive) && !!partialClient.formReferenceId;
  const {
    mode,
    setMode,
    save,
    isPending: isQuickEditPending,
    isRefreshing,
    clearRefreshing,
    intakeChangeRequest,
    latestApprovedRequest,
    approvalPolicy,
    isLoadingIntakeChangeRequest,
    isApprovalPolicyReady,
    isApprovalPromptOpen,
    confirmApprovalRequest,
    cancelApprovalRequest,
    editingRequestId,
    withdrawRequest,
    isWithdrawing,
    recalculateRequest,
    isRecalculatingRequest,
    recalculationError,
  } = useQuickEditIntake({
    clientId: partialClient.clientId,
    checkApprovalPolicy: !quickEstimateOnly,
    onSaveSuccess: quickEstimateOnly ? onClose : undefined,
    successMessage: quickEstimateOnly
      ? 'Quick estimate updated. Recalculating results.'
      : undefined,
  });

  const retryOnNoChanges =
    quickEstimateOnly && !!partialClient.clientTags?.includes(ALGORITHMS_ERROR);
  const { recalculateQuickEstimate, isRecalculating } = useRecalculateQuickEstimate({
    onSuccess: onClose,
  });
  const isSaving = isQuickEditPending || isRecalculating;
  const isCheckingApprovalPolicy = !quickEstimateOnly && isLoadingIntakeChangeRequest;

  const clientName = fullName(partialClient);
  const [withdrawalRequestId, setWithdrawalRequestId] = useState<string | null>(null);
  const [isQuickEditValid, setIsQuickEditValid] = useState(true);
  const [quickEditChanges, setQuickEditChanges] = useState<QuickEditChange[]>([]);
  const requiresApproval = approvalPolicy === 'approval-required';
  const hasApprovedUnappliedRequest = intakeChangeRequest?.status === 'approved';
  const approvedRequest = latestApprovedRequest ??
    (intakeChangeRequest?.status === 'approved' ? intakeChangeRequest : null);
  const pendingChangeCount = intakeChangeRequest?.changes.filter(
    change => change.questionRef !== 'clientHasDifferentLtcZipCode',
  ).length ?? 0;

  const clientDataReady =
    clientDataExists &&
    !clientIsLoading &&
    !typeformPayloadIsLoading &&
    (!typeformPayload || typeformPayload.clientId === partialClient.clientId);

  useEffect(() => {
    if (isRefreshing && clientDataReady) {
      clearRefreshing();
    }
  }, [isRefreshing, clientDataReady, clearRefreshing]);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      dispatch(fetchClientByClientIdRequest(partialClient.clientId));
      dispatch(fetchTypeformPayloadByClientIdRequest({ clientId: partialClient.clientId }));
    }
  }, [open, partialClient.clientId, dispatch, initialMode, setMode]);

  const handleClose = () => {
    setWithdrawalRequestId(null);
    setMode('view');
    onClose();
  };

  const title = quickEstimateOnly
    ? 'Edit Quick Estimate'
    : mode === 'edit'
      ? 'Quick Edit Mode'
      : (clientName ?? 'Client Information');

  const subTitle = retryOnNoChanges
    ? 'This estimate could not be calculated. Correct the details, or save without changes to try again.'
    : quickEstimateOnly
      ? 'Update the details this quick estimate is built from. Saving recalculates the estimate.'
      : mode === 'edit'
        ? 'Edit commonly updated fields below. Only a subset of intake fields can be modified here.'
        : `Information provided in ${clientName ? `${possessive(clientName)} ` : 'the '} intake form.`;

  const cancelEdit = quickEstimateOnly ? handleClose : () => setMode('view');

  const primaryButton =
    intakeChangeRequest?.status === 'pending' ? (
      <div className="flex w-full flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800">
            <Clock className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <p className="font-semibold text-gray-900">Awaiting client approval</p>
            <p className="text-sm text-gray-600">
              {pendingChangeCount} {pendingChangeCount === 1 ? 'change' : 'changes'} requested · {new Date(intakeChangeRequest.createdDateTime).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <DesignButton
            type="button"
            variant="outline"
            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
            disabled={isWithdrawing}
            onClick={() => setWithdrawalRequestId(intakeChangeRequest.id)}
          >
            {isWithdrawing ? 'Withdrawing…' : 'Withdraw changes'}
          </DesignButton>
          <IntakeChangeRequestChanges request={intakeChangeRequest} showSummary={false} reviewButton />
        </div>
      </div>
    ) : mode === 'edit' ? (
      <div className="flex w-full flex-col sm:w-auto">
        {!quickEstimateOnly && !isIntakeFormRestricted && (
          <div className="border-b pb-3 text-center">
            <button
              type="button"
              onClick={async () => {
                await navigateToIntakeForm();
                handleClose();
              }}
              disabled={isLoadingForm}
              className="text-primary hover:text-primary-hover inline-flex items-center gap-1 text-sm font-medium underline underline-offset-2 disabled:opacity-50"
            >
              {isLoadingForm ? (
                'Loading...'
              ) : (
                <>
                  Need to make additional changes? Access your full intake questionnaire
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="inline-block"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line
                      x1="10"
                      y1="14"
                      x2="21"
                      y2="3"
                    />
                  </svg>
                </>
              )}
            </button>
          </div>
        )}
        <div className={quickEstimateOnly || isIntakeFormRestricted ? 'flex gap-2' : 'flex gap-2 pt-4'}>
          <Button
            type="button"
            variant="outline"
            onClick={cancelEdit}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={QUICK_EDIT_FORM_ID}
            disabled={
              isSaving ||
              isCheckingApprovalPolicy ||
              !isApprovalPolicyReady ||
              !isQuickEditValid ||
              hasApprovedUnappliedRequest ||
              (requiresApproval && quickEditChanges.length === 0)
            }
          >
            {isSaving || isCheckingApprovalPolicy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isSaving ? 'Saving...' : 'Checking...'}
              </>
            ) : requiresApproval ? (
              <>
                Propose Changes
                <span
                  className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 text-xs font-semibold"
                  aria-label={`${quickEditChanges.length} proposed ${quickEditChanges.length === 1 ? 'change' : 'changes'}`}
                >
                  {quickEditChanges.length}
                </span>
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    ) : hasIntakeSurvey && !isQuickEditRestricted ? (
      <Button
        type="button"
        disabled={hasApprovedUnappliedRequest}
        onClick={() => setMode('edit')}
      >
        <Pencil className="mr-2 h-4 w-4" />
        {`Edit ${clientName ? `${possessive(clientName)}` : 'Client'} Data`}
      </Button>
    ) : undefined;

  return (
    <>
      <SheetModal
        open={open}
        onClose={handleClose}
        title={title}
        subTitle={subTitle}
        className="min-w-[95%] sm:min-w-[90%] md:min-w-[700px] lg:min-w-[900px] xl:min-w-[1000px]"
        primaryButton={primaryButton ? <div className="w-full pr-20">{primaryButton}</div> : undefined}
      >
        {approvedRequest && (
          <div className="my-4">
            <IntakeChangeRequestChanges request={approvedRequest} />
          </div>
        )}
        {latestApprovedRequest &&
          latestApprovedRequest.id !== intakeChangeRequest?.id &&
          (latestApprovedRequest.status === 'approved' ||
          (latestApprovedRequest.status === 'applied' &&
            latestApprovedRequest.quoteProjectionFailed) ? (
            <IntakeChangeRequestStatus
              request={latestApprovedRequest}
              onRecalculate={() => recalculateRequest(latestApprovedRequest.id)}
              isRecalculating={isRecalculatingRequest}
              recalculationError={recalculationError}
            />
          ) : null)}
        {intakeChangeRequest &&
          intakeChangeRequest.status !== 'pending' &&
          intakeChangeRequest.status !== 'withdrawn' &&
          !(
            intakeChangeRequest.status === 'applied' && !intakeChangeRequest.quoteProjectionFailed
          ) && (
            <IntakeChangeRequestStatus
              request={intakeChangeRequest}
              onRecalculate={() => recalculateRequest(intakeChangeRequest.id)}
              isRecalculating={isRecalculatingRequest}
              recalculationError={recalculationError}
            />
          )}
        <ClientDataModalContent
          mode={mode}
          clientDataReady={clientDataReady && !isRefreshing}
          quickEstimateOnly={quickEstimateOnly}
          onCancelEdit={cancelEdit}
          onSave={save}
          onNoChanges={
            retryOnNoChanges ? () => recalculateQuickEstimate(partialClient.clientId) : undefined
          }
          onValidChange={setIsQuickEditValid}
          onChangesChange={setQuickEditChanges}
          editingRequest={editingRequestId === intakeChangeRequest?.id ? intakeChangeRequest : null}
          onWithdrawEditedRequest={
            editingRequestId ? () => setWithdrawalRequestId(editingRequestId) : undefined
          }
        />
      </SheetModal>
      <AlertDialog
        open={
          open &&
          withdrawalRequestId !== null &&
          intakeChangeRequest?.id === withdrawalRequestId &&
          intakeChangeRequest.status === 'pending'
        }
        onOpenChange={nextOpen => {
          if (!nextOpen) setWithdrawalRequestId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw proposed changes?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the pending request. The client's current answers and plan will stay
              unchanged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep proposed changes</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/20"
              onClick={() => {
                if (
                  intakeChangeRequest?.status === 'pending' &&
                  intakeChangeRequest.id === withdrawalRequestId
                ) {
                  withdrawRequest(intakeChangeRequest.id);
                }
                setWithdrawalRequestId(null);
              }}
            >
              Withdraw changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <IntakeChangeApprovalDialog
        open={isApprovalPromptOpen}
        onCancel={cancelApprovalRequest}
        onConfirm={confirmApprovalRequest}
        changes={quickEditChanges}
      />
    </>
  );
}

function IntakeChangeApprovalDialog({
  open,
  onCancel,
  onConfirm,
  changes,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  changes: QuickEditChange[];
}) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) onCancel();
      }}
    >
      <AlertDialogContent className="sm:max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Send changes for client approval?</AlertDialogTitle>
          <AlertDialogDescription>
            The client will review these changes. Their current answers will stay in effect until
            they approve.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="max-h-[50vh] overflow-auto rounded-md border">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50 sticky top-0">
              <tr>
                <th className="px-4 py-2 font-medium">Change</th>
                <th className="px-4 py-2 font-medium">Current</th>
                <th className="px-4 py-2 font-medium">Proposed</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {changes.map(change => (
                <tr key={change.fieldName}>
                  <th className="px-4 py-3 font-medium">{change.label}</th>
                  <td className="text-muted-foreground px-4 py-3">{change.before}</td>
                  <td className="px-4 py-3 font-medium">{change.after}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>
            <Send
              className="mr-2 h-4 w-4"
              aria-hidden="true"
            />
            Send for approval
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function IntakeChangeRequestStatus({
  request,
  onRecalculate,
  isRecalculating,
  recalculationError,
}: {
  request: IntakeChangeRequestSummary;
  onRecalculate: () => void;
  isRecalculating: boolean;
  recalculationError: unknown;
}) {
  const applyFailed = request.status === 'approved';
  const quoteProjectionFailed = request.status === 'applied' && request.quoteProjectionFailed;
  const failed = applyFailed || quoteProjectionFailed;
  const descriptions: Partial<Record<IntakeChangeRequestStatus, string>> = {
    pending: 'Awaiting client approval',
    approved:
      'We could not apply these changes. The current answers and plan are unchanged. Try applying them again or contact support.',
    applied: quoteProjectionFailed
      ? 'The answers were updated, but the plan recommendations could not be recalculated.'
      : undefined,
    rejected: request.rejectionReason
      ? `Client reason: ${request.rejectionReason}`
      : 'The client rejected this request.',
    expired: 'This request expired. Create a new proposal if the change is still needed.',
    superseded: 'The client answers changed after this request was created.',
    withdrawn: 'This request was withdrawn.',
  };
  const description = descriptions[request.status];

  if (request.status === 'rejected') {
    return (
      <div className="text-muted-foreground my-4 flex items-start gap-2 text-sm">
        <X className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p>Last proposed changes rejected</p>
            <IntakeChangeRequestChanges request={request} showSummary={false} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Alert
        variant={failed ? 'warning' : 'info'}
        className="mb-4 mt-4"
      >
        {!failed ? <AlertTitle>Intake change request: {request.status}</AlertTitle> : null}
        {description ? <AlertDescription>{description}</AlertDescription> : null}
        {recalculationError ? (
          <AlertDescription>
            {recalculationError instanceof Error
              ? recalculationError.message
              : 'The plan could not be recalculated. Try again or contact support.'}
          </AlertDescription>
        ) : null}
        {failed ? (
          <div className="col-start-2 mt-3 flex flex-wrap items-center gap-4">
            <DesignButton
              type="button"
              disabled={isRecalculating}
              onClick={onRecalculate}
            >
              {applyFailed
                ? isRecalculating ? 'Applying…' : 'Apply approved changes'
                : isRecalculating ? 'Recalculating…' : 'Recalculate'}
            </DesignButton>
          </div>
        ) : null}
      </Alert>
    </>
  );
}

function ClientDataModalContent({
  mode,
  clientDataReady,
  quickEstimateOnly,
  onCancelEdit,
  onSave,
  onNoChanges,
  onValidChange,
  onChangesChange,
  editingRequest,
  onWithdrawEditedRequest,
}: {
  mode: 'view' | 'edit';
  clientDataReady: boolean;
  quickEstimateOnly: boolean;
  onCancelEdit: () => void;
  onSave: (fields: Record<string, string | number | null>) => void;
  onNoChanges?: () => void;
  onValidChange: (valid: boolean) => void;
  onChangesChange: (changes: QuickEditChange[]) => void;
  editingRequest: IntakeChangeRequestSummary | null;
  onWithdrawEditedRequest?: () => void;
}) {
  const client = useSelector(selectMaybeClient);

  if (!clientDataReady || !client) {
    return <ClientDataDescriptionList isLoading={true} />;
  }

  // A quick estimate whose model run failed has no calculated client data yet; fixing its answers
  // is exactly what this mode is for, so read the survey as the server sent it.
  const intakeSurvey = quickEstimateOnly ? findClientIntakeSurvey(client) : client.intakeSurvey;

  if (mode === 'edit' && intakeSurvey) {
    return (
      <QuickEditForm
        key={`${intakeSurvey.surveyVersionId}:${editingRequest?.id ?? 'new'}`}
        intakeSurvey={intakeSurvey}
        initialValues={
          editingRequest
            ? Object.fromEntries(
                editingRequest.changes.map(change => [
                  change.questionRef,
                  change.clientValueSet ? change.clientValue : change.advisorValue,
                ]),
              )
            : undefined
        }
        edjQuickPlan={quickEstimateOnly || usesEdjQuickPlanModelInput(client)}
        onCancel={onCancelEdit}
        onSave={onSave}
        onNoChanges={editingRequest ? onWithdrawEditedRequest : onNoChanges}
        onValidChange={onValidChange}
        onChangesChange={onChangesChange}
      />
    );
  }

  return <ClientDataDescriptionList isLoading={false} />;
}

interface DetailRowProps {
  label: string;
  value: string | React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
    <dt className="text-sm font-medium leading-6 text-gray-900">{label}</dt>
    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">{value}</dd>
  </div>
);

function ClientDataDescriptionList({ isLoading }: { isLoading: boolean }) {
  const client = useSelector(selectMaybeClient);
  const typeformPayload = useSelector(selectTypeformPayload);

  // Parse and merge typeform data with intakeSurvey
  const mergedSurveyData = useMemo(() => {
    if (!client?.intakeSurvey) {
      return null;
    }

    const merged = { ...client.intakeSurvey } as Record<string, any>;

    // Only merge if typeformPayload belongs to this client
    if (typeformPayload?.typeformPayload && typeformPayload.clientId === client.clientId) {
      try {
        const parsed = JSON.parse(typeformPayload.typeformPayload);

        Object.keys(parsed).forEach(key => {
          // Extract field name (before ::)
          const fieldName = key.split('::')[0];
          const schemaEntry = (clientIntakeFormSchema as Record<string, any>)[fieldName];
          if (!schemaEntry) return;

          // For list-type fields, prefer DB value when it's a non-empty array (includes "other" values)
          const existingValue = client.intakeSurvey[fieldName as keyof typeof client.intakeSurvey];
          if (schemaEntry.type === 'list' && isArray(existingValue) && existingValue.length > 0) {
            merged[fieldName] = existingValue;
          } else if (
            fieldName !== 'clientHeightInches' &&
            (QUICK_EDIT_ALL_FIELDS as readonly string[]).includes(fieldName) &&
            existingValue != null
          ) {
            // For quick-edit fields, prefer DB values (they may have been updated via quick edit)
            merged[fieldName] = existingValue;
          } else {
            merged[fieldName] = parsed[key];
          }
        });
      } catch (error) {
        console.error('Error parsing typeformPayload:', error);
      }
    }

    return merged;
  }, [client?.intakeSurvey, client?.clientId, typeformPayload]);

  if (isLoading || !client) {
    return (
      <div className="my-10 flex h-64 items-center justify-center">
        <LoadingSkeleton />
      </div>
    );
  }

  const { intakeSurvey, introIntakeSurvey } = client;

  const formatValue = (value: any, type: string, fieldName: string) => {
    switch (type) {
      case 'string': {
        if (!value) return 'N/A';
        const fieldLabels = PREFERENCE_DISPLAY_LABELS[fieldName];
        if (fieldLabels) {
          const key = typeof value === 'string' ? value.toLowerCase() : value;
          return fieldLabels[key] ?? value;
        }
        return value;
      }
      case 'integer':
        return value ? value.toString() : 'N/A';
      case 'number':
        return value ? `$${parseFloat(value).toLocaleString()}` : 'N/A';
      case 'percent':
        return value ? `${parseFloat(value)}%` : 'N/A';
      case 'list':
        if (!value) return 'None';
        // If it's an array, use as is; if it's a string, split by delimiter (|||)
        const arr = isArray(value)
          ? value
          : typeof value === 'string'
            ? value.split('|||').map(item => item.trim())
            : [value];
        if (arr.length < 1) return 'None';
        return (
          <ul className="list-inside list-disc">
            {arr.map((item: any, index: number) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        );
      case 'date':
        if (!value) return 'N/A';
        const date = new Date(value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: 'UTC',
        });
      default:
        return 'N/A';
    }
  };

  const renderSurveyDetails = <T extends Record<string, any>>(surveyData: T) => {
    const sortedDetails = Object.entries(clientIntakeFormSchema)
      .filter(([key]) => key in surveyData) // Ensures we only include fields present in surveyData
      .sort((a, b) => a[1].group.localeCompare(b[1].group));

    return sortedDetails.map(([key, { label, type }]) => (
      <DetailRow
        key={key}
        label={label}
        value={formatValue(surveyData[key as keyof T], type, key)}
      />
    ));
  };

  return (
    <div
      data-hj-suppress
      className="mt-6 "
      data-private
    >
      {introIntakeSurvey && (
        <>
          <h2 className="text-base font-semibold text-gray-700">Client Information</h2>
          <dl className="divide-y divide-gray-100">
            {renderSurveyDetails<IntroIntakeSurvey>(introIntakeSurvey)}
          </dl>
        </>
      )}
      <h2 className="mt-6 text-base font-semibold text-gray-700">Intake Survey Answers</h2>
      <dl
        data-testid="survey-details-renderer"
        className="divide-y divide-gray-100"
      >
        {mergedSurveyData || intakeSurvey ? (
          renderSurveyDetails<IntakeSurvey>((mergedSurveyData || intakeSurvey) as IntakeSurvey)
        ) : (
          <p className="py-4 text-sm text-gray-500">No intake survey data available.</p>
        )}
      </dl>
    </div>
  );
}

const LoadingSkeleton: React.FC = () => {
  return (
    <div
      role="status"
      className="w-full animate-pulse space-y-4 divide-y divide-gray-200 rounded  p-4  md:p-6 "
    >
      {[...Array(5)].map((_, index) => (
        <div
          key={index}
          className="flex items-center justify-between pt-4 first:pt-0"
        >
          <div>
            <div className="mb-2.5 h-2.5 w-24 rounded-full bg-gray-300 "></div>
            <div className="h-2 w-32 rounded-full bg-gray-200 "></div>
          </div>
          <div className="h-2.5 w-12 rounded-full bg-gray-300 "></div>
        </div>
      ))}
      <span className="sr-only">Loading...</span>
    </div>
  );
};
